import { ButtonProps } from '@carvana/ds-button';
import { useSubsequentEffect } from '@carvana/merch-react-hooks';
import cx from 'classnames';
import { useInView } from 'react-intersection-observer';
import { absoluteBaseUrl } from '~/constants/environment';
import {
  useDealershipId,
  useIsDealershipPage
} from '~/context/dealership/StaticContext';
import { useSectionAnalytics } from '~/context/vdp/SectionAnalyticsContext';
import { useStickyBanner } from '~/context/vdp/StickyBannerContext';
import useIsInExclusivePreview from '~/hooks/vdp/selectors/useIsInExclusivePreview';
import buildDealershipUrl from '~/utilities/dealership/buildDealershipUrl';
import DealershipCtaButton from '../CtaButton/DealershipCtaButton';
import DealershipPrimaryCtaButton from '../CtaButton/DealershipPrimaryCtaButton';
import DealershipTermsCtaButton from '../CtaButton/DealershipTermsCtaButton';
import CtaButton from '../CtaButton/PrimaryCtaButton';
import SecondaryCtaButton from '../CtaButton/SecondaryCtaButton';
import TextCtaButton from '../CtaButton/TextCtaButton';

// Apply gap between columns when first div has a button
const ctaGapClass = '[&:has(>div:first-child>button)]:gap-xl';

type OwnProps = {
  onClick?: () => void;
  size?: ButtonProps['size'];
  withSecondary?: boolean;
};

export default function MainCta({
  onClick,
  size = 'lg',
  withSecondary = true
}: OwnProps) {
  const dealershipId = useDealershipId();
  const isDealership = useIsDealershipPage();
  const isInExclusivePreview = useIsInExclusivePreview();
  const { analyticsAttributes, analyticsId } = useSectionAnalytics();
  const { setShouldShowStickyFooter } = useStickyBanner();

  const [ref, inView] = useInView({
    initialInView: true,
    // 60px offset margin is included to account for [StickyBanner](merch-ui/src/components/vdp/helios/StickyBanner/StickyBanner.tsx) height
    rootMargin: '-60px 0px',
    threshold: [0, 1]
  });

  // Skip the first render to avoid false positives
  useSubsequentEffect(() => {
    if (isDealership) {
      setShouldShowStickyFooter(!inView);
    }
  }, [inView, isDealership, setShouldShowStickyFooter]);

  return isDealership ? (
    <div ref={ref} className="flex flex-col gap-md">
      <DealershipPrimaryCtaButton
        data-analytics-id={analyticsId}
        data-analytics-attributes={analyticsAttributes}
        data-testid="get-started-button"
      />
      <DealershipCtaButton
        data-adobe-id="get-trade-in-offer"
        data-analytics-id={analyticsId}
        data-analytics-attributes={{
          ...analyticsAttributes,
          description: 'secondary cta'
        }}
        data-testid="dealership-hero-trade-button"
        href={buildDealershipUrl(
          `${absoluteBaseUrl}/sell-my-car`,
          dealershipId,
          'stc'
        )}
        text="Get trade-in offer"
        variant="secondary"
      />
      <DealershipTermsCtaButton
        data-analytics-id={analyticsId}
        data-analytics-attributes={{
          ...analyticsAttributes,
          description: 'secondary cta'
        }}
        data-testid="dealership-hero-terms-button"
      />
    </div>
  ) : (
    <div className={cx('flex w-full flex-col', ctaGapClass)}>
      <div className="flex w-full flex-wrap-reverse gap-md">
        {withSecondary && (
          <SecondaryCtaButton
            className="min-w-[180px] flex-1"
            data-analytics-id={analyticsId}
            data-analytics-attributes={analyticsAttributes}
            data-testid="hero-secondary-cta-button"
            onClick={onClick}
            size={size}
          />
        )}
        <CtaButton
          className={cx('min-w-[180px] flex-1', {
            'w-full': isInExclusivePreview
          })}
          data-analytics-id={analyticsId}
          data-analytics-attributes={analyticsAttributes}
          data-testid="get-started-button"
          onClick={onClick}
          size={size}
        />
      </div>
      <TextCtaButton
        className="w-full min-w-[180px] flex-1"
        data-analytics-id={analyticsId}
        data-analytics-attributes={analyticsAttributes}
        data-testid="hero-text-button"
        onClick={onClick}
        size="md"
      />
    </div>
  );
}
